import React, { useState, useCallback } from 'react';
import Modal, { ModalFooter } from '@amzn/meridian/modal';
import Column from '@amzn/meridian/column';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';
import Button from '@amzn/meridian/button';
import Input from '@amzn/meridian/input';
import Textarea from '@amzn/meridian/textarea';
import Select, { SelectOption } from '@amzn/meridian/select';
import Alert from '@amzn/meridian/alert';
import {
  ordisAPIService,
  BugReportCategory,
  BugReportSeverity,
  APIRequestError,
} from '../../service/ordisApiService';

const TITLE_MAX = 200;
const DESCRIPTION_MAX = 5000;

export interface BugReportModalProps {
  open: boolean;
  onClose: () => void;
  /** Called with the new report id after a successful submit. */
  onSubmitted?: (reportId: string) => void;
}

/**
 * "Report a bug" form (spec ordis-bug-report). A Meridian modal with a
 * category, short title, description, and optional severity. Client-side
 * validation gates the submit button; the server re-validates and sets the
 * reporter identity from the verified Midway alias.
 */
const BugReportModal: React.FC<BugReportModalProps> = ({ open, onClose, onSubmitted }) => {
  const [category, setCategory] = useState<BugReportCategory>('bug');
  const [severity, setSeverity] = useState<BugReportSeverity>('medium');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);

  const titleValid = title.trim().length > 0 && title.length <= TITLE_MAX;
  const descriptionValid =
    description.trim().length > 0 && description.length <= DESCRIPTION_MAX;
  const canSubmit = titleValid && descriptionValid && !busy;

  const reset = useCallback(() => {
    setCategory('bug');
    setSeverity('medium');
    setTitle('');
    setDescription('');
    setError(undefined);
    setBusy(false);
  }, []);

  const handleClose = useCallback(() => {
    if (busy) return;
    reset();
    onClose();
  }, [busy, reset, onClose]);

  const handleSubmit = useCallback(async () => {
    if (!canSubmit) return;
    setBusy(true);
    setError(undefined);
    try {
      const { report_id } = await ordisAPIService.reportBug({
        title: title.trim(),
        description: description.trim(),
        category,
        severity,
      });
      reset();
      onClose();
      onSubmitted?.(report_id);
    } catch (e) {
      // Keep the form content so the user can retry; surface the message.
      const message =
        e instanceof APIRequestError
          ? e.serverMessage || `Could not submit (status ${e.status}).`
          : 'Could not submit your report. Please try again.';
      setError(message);
      setBusy(false);
    }
  }, [canSubmit, title, description, category, severity, reset, onClose, onSubmitted]);

  return (
    <Modal
      open={open}
      title="Report a bug or issue"
      onClose={handleClose}
      scrollContainer="modal"
      aria-describedby="bug-report-body"
    >
      <div id="bug-report-body">
        <Column spacing="400">
          {error && (
            <Alert type="error" size="small">
              {error}
            </Alert>
          )}

          <Column spacing="200">
            <Text type="b200">Type</Text>
            <Select
              value={category}
              onChange={(v: BugReportCategory) => setCategory(v)}
              size="small"
              placeholder="Select a type"
            >
              <SelectOption value="bug" label="Bug" />
              <SelectOption value="feature_request" label="Feature request" />
              <SelectOption value="other" label="Other" />
            </Select>
          </Column>

          <Column spacing="200">
            <Text type="b200">Severity</Text>
            <Select
              value={severity}
              onChange={(v: BugReportSeverity) => setSeverity(v)}
              size="small"
              placeholder="Select a severity"
            >
              <SelectOption value="low" label="Low" />
              <SelectOption value="medium" label="Medium" />
              <SelectOption value="high" label="High" />
            </Select>
          </Column>

          <Column spacing="200">
            <Text type="b200">Title</Text>
            <Input
              value={title}
              onChange={setTitle}
              placeholder="Short summary"
              size="small"
              type="text"
            />
          </Column>

          <Column spacing="200">
            <Text type="b200">Description</Text>
            <Textarea
              value={description}
              onChange={setDescription}
              placeholder="What happened? What did you expect? Steps to reproduce, if any."
              rows={5}
            />
            <Text type="b100" color={description.length > DESCRIPTION_MAX ? 'error' : 'secondary'}>
              {description.length}/{DESCRIPTION_MAX}
            </Text>
          </Column>
        </Column>
      </div>

      <ModalFooter>
        <Row alignmentHorizontal="end" widths="fit">
          <Button type="tertiary" onClick={handleClose} disabled={busy}>
            Cancel
          </Button>
          <Button type="primary" onClick={handleSubmit} disabled={!canSubmit}>
            {busy ? 'Submitting…' : 'Submit report'}
          </Button>
        </Row>
      </ModalFooter>
    </Modal>
  );
};

export default BugReportModal;
